import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { PlanView } from '../components/forms/s3/PlanView';
import { errorText, useApi } from '../lib/api';
import { useEnsureDraft } from '../lib/contentConfig';
import {
  isNotAvailable,
  overwatchAttempt,
  overwatchAttempts,
  type OverwatchAttemptDetail,
  type OverwatchAttemptRow,
} from '../lib/s3Api';
import type { OverwatchDef, OverwatchPerchDef } from '../lib/s3/types';

const TAKE = 50;
const STATUSES = ['issued', 'accepted', 'rejected'] as const;
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/50 disabled:opacity-40';
const statusCls: Record<string, string> = {
  accepted: 'bg-benday/40',
  rejected: 'bg-blam text-newsprint',
  issued: 'bg-caption',
};

/**
 * One attempt's shot log on its scene's plan (from the draft content): each round drawn from its perch along the logged yaw (the verdict
 * carries no per-shot impact), the shot table with the client's claims, and the server's replay verdict beside it.
 */
export function ShotLog({
  detail,
  overwatch,
  maxRangeM = 300,
}: {
  detail: OverwatchAttemptDetail;
  overwatch?: OverwatchDef | null;
  /** How far an unknown round is drawn: the weapon's scope range. */
  maxRangeM?: number;
}) {
  const a = detail.attempt;
  const scene = (overwatch?.scenes ?? []).find((s) => s?.id === a.sceneId);
  const mission = (overwatch?.missions ?? []).find((m) => m?.id === a.missionId);
  const shots = detail.result?.shots ?? [];
  const v = detail.verdict;
  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
      {scene ? (
        <PlanView
          plan={scene.plan}
          perches={(scene.perches ?? []) as OverwatchPerchDef[]}
          maxRangeM={maxRangeM}
          shots={shots.map((s, i) => ({
            perchId: s.perchId,
            yawDeg: s.yawDeg,
            hit: !!s.claimedHit,
            head: !!s.claimedHead,
            label: String(i + 1),
          }))}
          label={`Shot log of ${a.id}`}
        />
      ) : (
        <p className="text-sm text-bruise">
          Scene &apos;{a.sceneId}&apos; isn&apos;t in the draft, so the plan can&apos;t be drawn.
        </p>
      )}
      <div className="space-y-2 self-start">
        <table className="w-full border-2 border-ink bg-white text-xs">
          <caption className="bg-ink px-2 py-1 text-left font-bold text-newsprint">
            {shots.length} shot(s){mission?.shotCap ? ` of ${mission.shotCap}` : ''}
          </caption>
          <thead>
            <tr className="text-left">
              <th className="px-1">#</th>
              <th className="px-1">t s</th>
              <th className="px-1">yaw / pitch</th>
              <th className="px-1">zoom</th>
              <th className="px-1">perch</th>
              <th className="px-1">claimed</th>
            </tr>
          </thead>
          <tbody>
            {shots.map((s, i) => (
              <tr key={i} className="border-t border-newsprint">
                <td className="px-1 font-mono">{i + 1}</td>
                <td className="px-1 font-mono">{(s.t ?? 0).toFixed(2)}</td>
                <td className="px-1 font-mono">
                  {(s.yawDeg ?? 0).toFixed(2)}° / {(s.pitchDeg ?? 0).toFixed(2)}°
                </td>
                <td className="px-1">{s.zoom}x</td>
                <td className="px-1 font-mono">{s.perchId}</td>
                <td className="px-1">
                  {s.claimedHit || 'miss'}
                  {s.claimedHead ? ' (head)' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {v ? (
          <dl className="grid grid-cols-2 gap-x-2 rounded border-2 border-ink bg-white p-2 text-xs">
            <dt className="font-semibold">Server verdict</dt>
            <dd>
              {v.ending} · {v.score} ({v.grade || '-'})
            </dd>
            <dt>Hits / heads / kills</dt>
            <dd>
              {v.hits} / {v.headshots} / {v.kills} of {v.shotsFired}
            </dd>
            <dt>Phases won</dt>
            <dd>{(v.phasesWon ?? []).join(', ') || 'none'}</dd>
            <dt>Innocents</dt>
            <dd className={v.innocents ? 'text-blam' : ''}>
              {v.innocents}
              {v.innocentIds?.length ? ` (${v.innocentIds.join(', ')})` : ''}
            </dd>
            <dt>Saved</dt>
            <dd>{v.saved}</dd>
            <dt>Caught the bitten one</dt>
            <dd>{v.caughtBitten ? 'yes' : 'no'}</dd>
            {detail.crate?.length ? (
              <>
                <dt>Crate</dt>
                <dd>{detail.crate.map((c) => `${c.quantity} ${c.itemId}`).join(', ')}</dd>
              </>
            ) : null}
          </dl>
        ) : (
          <p className="text-xs text-bruise">No verdict yet.</p>
        )}
        {detail.block?.hidden ? (
          <p className="font-mono text-xs">
            Hidden:{' '}
            {Object.entries(detail.block.hidden)
              .map(([k, x]) => `${k}=${x}`)
              .join(', ')}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function AttemptLog({
  row,
  overwatch,
  maxRangeM,
}: {
  row: OverwatchAttemptRow;
  overwatch?: OverwatchDef | null;
  maxRangeM?: number;
}) {
  const { client } = useApi();
  const q = useQuery({
    queryKey: ['overwatch-attempt', row.id],
    queryFn: () => overwatchAttempt(client, row.id),
    retry: false,
  });
  if (q.isPending) return <p className="text-xs">Loading the shot log&hellip;</p>;
  if (q.error) return <p className="text-xs text-blam">{errorText(q.error)}</p>;
  return <ShotLog detail={q.data} overwatch={overwatch} maxRangeM={maxRangeM} />;
}

/** Overwatch attempts (sniper.html "Admin"): every sniper mission played, the server's verdict, and the shot log on the plan. */
export function OverwatchAttemptsPage() {
  const { client } = useApi();
  const { draft } = useEnsureDraft();
  const [playerId, setPlayerId] = useState('');
  const [missionId, setMissionId] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['overwatch-attempts', playerId, missionId, status, offset],
    queryFn: () =>
      overwatchAttempts(client, {
        playerId: playerId.trim() || undefined,
        missionId: missionId || undefined,
        status: status || undefined,
        offset,
        take: TAKE,
      }),
    retry: false,
  });
  const ow = draft?.bundle.overwatch as OverwatchDef | undefined;
  const missions = (ow?.missions ?? []).map((m) => m?.id ?? '').filter(Boolean);
  const rows = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const reset = () => setOffset(0);
  return (
    <>
      <PageHeader
        title="Overwatch attempts"
        description="Every sniper mission played: who, which mission and perch, the status, the server's ending, score and grade, and the shot log on the scene plan, for support and balancing."
      />
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <input
          aria-label="Player id"
          placeholder="player id"
          className="rounded border-2 border-ink px-2 py-1 font-mono"
          value={playerId}
          onChange={(e) => {
            setPlayerId(e.target.value);
            reset();
          }}
        />
        <select
          aria-label="Mission"
          className="rounded border-2 border-ink px-2 py-1"
          value={missionId}
          onChange={(e) => {
            setMissionId(e.target.value);
            reset();
          }}
        >
          <option value="">any mission</option>
          {missions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          aria-label="Status"
          className="rounded border-2 border-ink px-2 py-1"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            reset();
          }}
        >
          <option value="">any status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      {q.isPending && <p>Loading&hellip;</p>}
      {q.error && (
        <p className="text-sm text-blam">
          {isNotAvailable(errorText(q.error))
            ? 'Not available yet: the attempts endpoint comes with S3-OWS.'
            : errorText(q.error)}
        </p>
      )}
      {q.data && (
        <>
          <table className="w-full border-2 border-ink bg-white text-sm">
            <thead className="bg-ink text-newsprint">
              <tr>
                <th className="px-2 py-1 text-left">When</th>
                <th className="px-2 py-1 text-left">Player</th>
                <th className="px-2 py-1 text-left">Mission</th>
                <th className="px-2 py-1 text-left">Perch</th>
                <th className="px-2 py-1 text-left">Status</th>
                <th className="px-2 py-1 text-left">Ending</th>
                <th className="px-2 py-1 text-left">Score</th>
                <th className="px-2 py-1 text-left">Shots</th>
                <th className="px-2 py-1" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-2 py-2 text-bruise">
                    No attempts.
                  </td>
                </tr>
              )}
              {rows.map((a) => (
                <Fragment key={a.id}>
                  <tr className="border-t border-newsprint">
                    <td className="px-2 py-1">{new Date(a.createdAt).toLocaleString()}</td>
                    <td className="px-2 py-1">
                      <Link
                        to="/players/$playerId"
                        params={{ playerId: a.playerId }}
                        className="font-mono underline"
                      >
                        {a.playerId.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-2 py-1 font-mono">{a.missionId}</td>
                    <td className="px-2 py-1 font-mono">
                      {a.perchId} <span className="text-xs text-bruise">{a.weaponId}</span>
                    </td>
                    <td className="px-2 py-1">
                      <span
                        className={`rounded px-1 text-xs font-semibold ${statusCls[a.status] ?? ''}`}
                      >
                        {a.status}
                      </span>
                      {a.countedWin ? <span className="ml-1 text-xs">win</span> : null}
                    </td>
                    <td className="px-2 py-1">{a.ending || '-'}</td>
                    <td className="px-2 py-1">
                      {a.score}
                      {a.grade ? ` (${a.grade})` : ''}
                      {a.innocents ? (
                        <span className="ml-1 text-xs text-blam">{a.innocents} innocent</span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1">{a.shotsFired}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        className={btn}
                        onClick={() => setOpen(open === a.id ? null : a.id)}
                      >
                        {open === a.id ? 'Hide' : 'Shot log'}
                      </button>
                    </td>
                  </tr>
                  {open === a.id && (
                    <tr>
                      <td colSpan={9} className="p-2">
                        <AttemptLog
                          row={a}
                          overwatch={ow}
                          maxRangeM={
                            (draft?.bundle.items ?? []).find((i) => i?.id === a.weaponId)?.weapon
                              ?.scope?.maxRangeM ?? 300
                          }
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex items-center gap-2 text-xs">
            <button
              type="button"
              className={btn}
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - TAKE))}
            >
              Previous
            </button>
            <span>
              {total ? `${offset + 1}–${Math.min(offset + TAKE, total)} of ${total}` : '0'}
            </span>
            <button
              type="button"
              className={btn}
              disabled={offset + TAKE >= total}
              onClick={() => setOffset(offset + TAKE)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </>
  );
}
